// Actualizar origen - destino (requerimiento 2026-10-22): replica en los traslados seleccionados el origen y destino
// vigentes de su autorización (Ida directo, Retorno invertido). Primero muestra el resumen (seleccionados, a actualizar,
// excluidos y motivos) y, al confirmar, el resultado por traslado. Se usa en Centro de Control, Coordinación y
// Autorizaciones > Ver traslados.
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Table, Tag, Alert, Statistic, Row, Col, Spin, Button, Typography, message } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import api from "../../../axiosConfig";

const { Text } = Typography;

const RESULTADOS = {
  actualizable: { color: "blue", texto: "Se actualizará" },
  actualizado: { color: "green", texto: "Actualizado" },
  excluido: { color: "orange", texto: "Excluido" },
  fallido: { color: "red", texto: "Error" },
};

const ActualizarOrigenDestinoDialog = ({ open, onClose, trasladoIds, modulo, onActualizado }) => {
  const [cargando, setCargando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  // Misma clave en los reintentos de esta confirmación: el backend no repite la operación
  const operacionId = useMemo(() => (open ? crypto.randomUUID() : null), [open]);

  useEffect(() => {
    if (!open) return;
    let vigente = true;
    setResumen(null);
    setResultado(null);
    setError(null);
    setCargando(true);
    api
      .post("/traslados/origen-destino/previsualizar", { ids: trasladoIds, modulo })
      .then((r) => vigente && setResumen(r.data.data))
      .catch((e) => vigente && setError(e.response?.data?.message || "No se pudo calcular el resumen"))
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, [open, trasladoIds, modulo]);

  const aplicar = async () => {
    setAplicando(true);
    try {
      const r = await api.post("/traslados/origen-destino/aplicar", { ids: trasladoIds, modulo, operacion_id: operacionId });
      setResultado(r.data.data);
      message.success(r.data.message || "Origen y destino actualizados");
      if (onActualizado) onActualizado(r.data.data);
    } catch (e) {
      message.error(e.response?.data?.message || "Error al actualizar el origen y destino");
    } finally {
      setAplicando(false);
    }
  };

  const datos = resultado || resumen;
  const columnas = [
    { title: "ID", dataIndex: "id", key: "id", width: 90 },
    {
      title: "Trayecto",
      dataIndex: "trayecto",
      key: "trayecto",
      width: 95,
      render: (t) => (t ? <Tag color={t === "Retorno" ? "purple" : "geekblue"}>{t}</Tag> : "-"),
    },
    { title: "Estado", dataIndex: "status", key: "status", width: 110, render: (s) => s || "-" },
    {
      title: "Origen",
      key: "origen",
      render: (_, r) => <CambioLado antes={r.antes?.origen} despues={r.despues?.origen} />,
    },
    {
      title: "Destino",
      key: "destino",
      render: (_, r) => <CambioLado antes={r.antes?.destino} despues={r.despues?.destino} />,
    },
    {
      title: "Resultado",
      key: "resultado",
      width: 170,
      render: (_, r) => (
        <>
          <Tag color={RESULTADOS[r.resultado]?.color}>{RESULTADOS[r.resultado]?.texto || r.resultado}</Tag>
          {r.motivo && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {r.motivo}
              </Text>
            </div>
          )}
        </>
      ),
    },
  ];

  const pie = resultado
    ? [<Button key="cerrar" type="primary" onClick={onClose}>Cerrar</Button>]
    : [
        <Button key="cancelar" onClick={onClose} disabled={aplicando}>Cancelar</Button>,
        <Button key="aplicar" type="primary" loading={aplicando} disabled={!resumen || resumen.actualizables === 0} onClick={aplicar}>
          {resumen ? `Actualizar ${resumen.actualizables} traslado(s)` : "Actualizar"}
        </Button>,
      ];

  return (
    <Modal
      open={open}
      onCancel={aplicando ? undefined : onClose}
      title={<span><SwapOutlined /> Actualizar origen - destino</span>}
      width={1100}
      footer={pie}
      maskClosable={false}
      destroyOnClose
    >
      {cargando && <div style={{ textAlign: "center", padding: 32 }}><Spin /></div>}
      {error && <Alert type="error" showIcon message={error} />}
      {datos && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              resultado
                ? "Resultado de la operación"
                : "Se copiarán el origen y destino vigentes de la autorización de cada traslado. En los traslados de Retorno se copian invertidos, como al programar. No cambia el estado ni otros datos del traslado."
            }
          />
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col span={6}><Statistic title="Seleccionados" value={datos.seleccionados} /></Col>
            <Col span={6}>
              <Statistic
                title={resultado ? "Actualizados" : "Se actualizarán"}
                value={resultado ? datos.actualizados : datos.actualizables}
                valueStyle={{ color: "#3f8600" }}
              />
            </Col>
            <Col span={6}><Statistic title="Excluidos" value={datos.excluidos} valueStyle={{ color: "#d46b08" }} /></Col>
            {resultado && <Col span={6}><Statistic title="Con error" value={datos.fallidos} valueStyle={{ color: "#cf1322" }} /></Col>}
          </Row>
          {Object.keys(datos.motivos || {}).length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Motivos de exclusión"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {Object.entries(datos.motivos).map(([motivo, cantidad]) => (
                    <li key={motivo}>{motivo}: {cantidad}</li>
                  ))}
                </ul>
              }
            />
          )}
          {(datos.avisos || []).map((aviso) => (
            <Alert key={aviso} type="warning" showIcon style={{ marginBottom: 12 }} message={aviso} />
          ))}
          <Table
            rowKey="id"
            size="small"
            columns={columnas}
            dataSource={datos.traslados}
            pagination={datos.traslados.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
            scroll={{ x: 900 }}
          />
        </>
      )}
    </Modal>
  );
};

const CambioLado = ({ antes, despues }) => (
  <div style={{ fontSize: 12, lineHeight: 1.4 }}>
    <div><Text type="secondary">Antes: </Text>{antes || "-"}</div>
    {despues && <div><Text type="success">Nuevo: </Text>{despues}</div>}
  </div>
);

CambioLado.propTypes = {
  antes: PropTypes.string,
  despues: PropTypes.string,
};

ActualizarOrigenDestinoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trasladoIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  modulo: PropTypes.oneOf(["Centro de Control", "Coordinacion", "Autorizaciones"]).isRequired,
  onActualizado: PropTypes.func,
};

export default ActualizarOrigenDestinoDialog;
